import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheck, FiZap } from 'react-icons/fi';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';
import Navbar from './Navbar';

const AdminPanel = () => {
  const [offers, setOffers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentOffer, setCurrentOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    logo: '',
    name: '',
    offerId: '',
    userEventsAmount: [''],
    referEventsAmount: [''],
    events: [''],
    trackLink: '',
    userComment: '',
    referComment: '',
    terms: '',
    selfRefer: false,
    status: 'active',
    oneDevice: true,
    totalCap: '',
    dailyCap: ''
  });

  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  };

  const checkAuth = async () => {
    try {
      const token = getCookie('offerAdminAuth');
      if (!token) {
        navigate('/adminBT/login');
        return;
      }

      const res = await axios.post('https://offers.cashbolt.in.net/server/', {
        action: 'checkAuth',
        authToken: token
      });

      if (!res.data.status) {
        navigate('/adminBT/login');
      } else {
        setAuthChecked(true);
      }
    } catch (error) {
      navigate('/adminBT/login');
    }
  };

  const fetchOffers = async () => {
    const token = getCookie('offerAdminAuth');
    try {
      const res = await axios.post('https://offers.cashbolt.in.net/server/', {
        action: 'fetchOffers',
        authToken: token
      });
      if (res.data.status) {
        setOffers(res.data.data);
      } else {
        console.error('Fetch failed:', res.data.message);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth().then(() => {
      if (authChecked) {
        fetchOffers();
      }
    });
  }, [authChecked]);


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEventChange = (index, field, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData(prev => ({
      ...prev,
      [field]: newArray
    }));
  };

  const addEventField = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeEventField = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      [field]: newArray
    }));
  };

  const openModal = (offer = null) => {
    setCurrentOffer(offer);
    if (offer) {
      setFormData({
        ...offer,
        events: offer.events ? offer.events.split(',') : [''],
        userEventsAmount: offer.userEventsAmount ? offer.userEventsAmount.split(',') : [''],
        referEventsAmount: offer.referEventsAmount ? offer.referEventsAmount.split(',') : ['']
      });
    } else {
      setFormData({
        logo: '',
        name: '',
        offerId: '',
        userEventsAmount: [''],
        referEventsAmount: [''],
        events: [''],
        trackLink: '',
        userComment: '',
        referComment: '',
        selfRefer: false,
        status: 'active',
        oneDevice: true,
        totalCap: '',
        dailyCap: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentOffer(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getCookie('offerAdminAuth');
    const action = currentOffer ? 'updateOffer' : 'addOffer';

    const payload = {
      ...formData,
      events: formData.events.join(','),
      userEventsAmount: formData.userEventsAmount.join(','),
      referEventsAmount: formData.referEventsAmount.join(',')
    };

    try {
      const res = await axios.post('https://offers.cashbolt.in.net/server/', {
        action,
        authToken: token,
        ...payload
      });

      if (res.data.status) {
        if (currentOffer) {
          setOffers(offers.map(offer =>
            offer.offerId === formData.offerId ? payload : offer
          ));
        } else {
          const newOffer = {
            ...payload,
            id: Date.now().toString(),
            createdAt: new Date().toISOString()
          };
          setOffers([...offers, newOffer]);
        }
        closeModal();
      } else {
        console.error('Save failed:', res.data.message);
      }
    } catch (err) {
      console.error('Error saving offer:', err);
    }
  };

  const deleteOffer = async (offerId) => {
    const token = getCookie('offerAdminAuth');
    try {
      const res = await axios.post('https://offers.cashbolt.in.net/server/', {
        action: 'deleteOffer',
        authToken: token,
        offerId
      });
      if (res.data.status) {
        setOffers(offers.filter(offer => offer.offerId !== offerId));
      } else {
        console.error('Delete failed:', res.data.message);
      }
    } catch (err) {
      console.error('Error deleting offer:', err);
    }
  };

  if (!authChecked || loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <Navbar />
      <header className="admin-header">
        <h1>Offer Management</h1>
        <motion.button
          className="add-offer-btn"
          onClick={() => openModal()}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiPlus /> Add New Offer
        </motion.button>
      </header>

      <div className="offers-table-container">
        <table className="offers-table">
          <thead>
            <tr>
              <th>Logo</th>
              <th>Name</th>
              <th>User Amount</th>
              <th>Refer Amount</th>
              <th>Total Cap</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((offer) => (
              <motion.tr
                key={offer.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <td>
                  <div className="offer-logo" style={{ backgroundColor: `var(--primary)` }}>
                    {offer.logo ? (
                      <img
                        src={offer.logo}
                        alt={offer.name}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="logo-fallback" style={{ display: offer.logoImage ? 'none' : 'flex' }}>
                      {offer.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                </td>
                <td>{offer.name}</td>
                <td>{offer.userEventsAmount}</td>
                <td>{offer.referEventsAmount}</td>
                <td>{offer.totalCap}</td>
                <td>
                  <span className={`payout-badge ${offer.status}`}>
                    {offer.status}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="edit-btn"
                      onClick={() => openModal(offer)}
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => deleteOffer(offer.offerId)}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="offer-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{currentOffer ? 'Edit Offer' : 'Add New Offer'}</h2>
                <button className="close-modal" onClick={closeModal}>
                  <FiX />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="input-section">
                    <div className="form-group">
                      <label>Logo Link</label>
                      <input
                        type="text"
                        name="logo"
                        value={formData.logo}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Offer Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Offer ID</label>
                      <input
                        type="text"
                        name="offerId"
                        value={formData.offerId}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Tracking Link</label>
                      <input
                        type="text"
                        name="trackLink"
                        value={formData.trackLink}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    {formData.events.map((event, index) => (
                      <div key={index} className="form-group event-field-group">
                        <label>Event {index + 1}</label>
                        <div className="event-input-row">
                          <input
                            type="text"
                            value={event}
                            onChange={(e) => handleEventChange(index, 'events', e.target.value)}
                            placeholder="Event name"
                            required
                          />
                          <input
                            type="text"
                            value={formData.userEventsAmount[index] || ''}
                            onChange={(e) => handleEventChange(index, 'userEventsAmount', e.target.value)}
                            placeholder="User amount"
                            required
                          />
                          <input
                            type="text"
                            value={formData.referEventsAmount[index] || ''}
                            onChange={(e) => handleEventChange(index, 'referEventsAmount', e.target.value)}
                            placeholder="Refer amount"
                            required
                          />
                          {index > 0 && (
                            <button
                              type="button"
                              className="remove-event-btn"
                              onClick={() => {
                                removeEventField('events', index);
                                removeEventField('userEventsAmount', index);
                                removeEventField('referEventsAmount', index);
                              }}
                            >
                              <FiX />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="add-event-btn"
                      onClick={() => {
                        addEventField('events');
                        addEventField('userEventsAmount');
                        addEventField('referEventsAmount');
                      }}
                    >
                      <FiPlus /> Add Event
                    </button>
                  </div>

                  <div className="input-section">
                    <div className="form-group">
                      <label>User Comment</label>
                      <textarea
                        name="userComment"
                        value={formData.userComment}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Referrer Comment</label>
                      <textarea
                        name="referComment"
                        value={formData.referComment}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Total Cap</label>
                      <input
                        type="number"
                        name="totalCap"
                        value={formData.totalCap}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Daily Cap</label>
                      <input
                        type="number"
                        name="dailyCap"
                        value={formData.dailyCap}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>Steps ( Use Commma for multiple Steps ) </label>
                      <input
                        type="text"
                        name="terms"
                        value={formData.terms}
                        onChange={handleInputChange}
                      />
                    </div>


                    <div className="toggle-group">
                      <label>Self Referral</label>
                      <div className="toggle-container">
                        <button
                          type="button"
                          className={`toggle-option ${formData.selfRefer ? 'active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, selfRefer: true }))}
                        >
                          <FiCheck /> Enabled
                        </button>
                        <button
                          type="button"
                          className={`toggle-option ${!formData.selfRefer ? 'active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, selfRefer: false }))}
                        >
                          <FiX /> Disabled
                        </button>
                      </div>
                    </div>

                    <div className="toggle-group">
                      <label>One Device</label>
                      <div className="toggle-container">
                        <button
                          type="button"
                          className={`toggle-option ${formData.oneDevice ? 'active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, oneDevice: true }))}
                        >
                          <FiCheck /> Enabled
                        </button>
                        <button
                          type="button"
                          className={`toggle-option ${!formData.oneDevice ? 'active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, oneDevice: false }))}
                        >
                          <FiX /> Disabled
                        </button>
                      </div>
                    </div>

                    <div className="toggle-group">
                      <label>Status</label>
                      <div className="toggle-container">
                        <button
                          type="button"
                          className={`toggle-option ${formData.status === 'active' ? 'active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, status: 'active' }))}
                        >
                          <FiZap /> Active
                        </button>
                        <button
                          type="button"
                          className={`toggle-option ${formData.status === 'paused' ? 'active' : ''}`}
                          onClick={() => setFormData(prev => ({ ...prev, status: 'paused' }))}
                        >
                          <FiX /> Paused
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    {currentOffer ? 'Update Offer' : 'Create Offer'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;